import * as yup from "yup";
import Bowser from "bowser";
import React, { useEffect, useRef, useState } from "react";
import MaskedTextField from "@/components/WebForm/FormUI/MaskedTextField";
import Grid2 from "@mui/material/Grid2";
import queryString from "query-string";
import TermsOfUseModal from "../../FormUI/TermsOfUseModal";

import {
  IAdm,
  IFormMetaData,
  useDataFormContext,
} from "@/context/FormDataContext";
import { toBeBrPhoneNumber, updatePhoneMask } from "@/libs/utils";
import { IRegisterResponse } from "@/services/index";
import { register } from "@/services/lead";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { ErrorMessage, Form, Formik } from "formik";
import {
  LinkStyled,
  SpanStyled,
  SpanStyledContainer,
  WrapperStyled,
} from "./styles";
import { listGroups } from "@/services/group";

const mandatory = "Campo obrigatório";

/**
 *
 * @returns PersonalDetails components
 */
export default function PersonalDetails(): React.JSX.Element {
  const {
    dataForm,
    admsList,
    metaDataTimes,
    setMetaDataTimes,
    setCurrentAdm,
    setCurrentFormStep,
    setCurrentFormVariation,
    setDataForm,
    setIsPj,
    setAskForStatement,
    setAlreadyShowed,
  } = useDataFormContext();

  const [modal, setModal] = useState<boolean>(false);
  const [cellPhoneMask, setCellPhoneMask] = useState<string>("(99) 9999-9999");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [admName, setAdmName] = useState<string>("");

  const query = queryString.parse(window.location.search);
  const infos = Bowser.getParser(window.navigator.userAgent).getResult();
  const INITIAL_FORM_STATE = {
    name: dataForm.name || "",
    cellPhone: dataForm.phone || "",
    email: dataForm.email || "",
    admId: dataForm.admId || 0,
    isCompany: dataForm.isPj ? "y" : "n",
    isAwarded: dataForm.isAwarded || "",
    companyName: dataForm.companyName || "",
    termsOfUse: dataForm.acceptPrivacyPolicy || false,
  };

  const FORM_VALIDATION = yup.object().shape({
    name: yup.string().min(5, "Nome inválido").required(mandatory),
    cellPhone: yup
      .string()
      .test("toBeBrPhoneNumber", "Telefone Inválido", function (x) {
        const plainValue = x && x.replace(/[^\d]/g, "");
        return toBeBrPhoneNumber(plainValue);
      })
      .required(mandatory),
    email: yup.string().email("Insira um email válido").required(mandatory),

    isCompany: yup.string().required(mandatory),
    companyName: yup.string().when("isCompany", {
      is: (val: string) => val === "n",
      then: (schema) => schema,
      otherwise: (schema) => schema.required(mandatory).min(5, "Nome inválido"),
    }),
    isAwarded: yup
      .string()
      .required(mandatory)
      .test("isAwarded", "Campo deve ser preenchido", function (x) {
        return x === "y" || x === "n";
      }),
    termsOfUse: yup
      .boolean()
      .oneOf([true], "Campo deve ser preenchido")
      .required("Campo deve ser preenchido"),
    ...(admsList.length > 1 && {
      admId: yup
        .number()
        .moreThan(0, "Escolha uma administradora")
        .required(mandatory),
    }),
  });

  useEffect(() => {
    setAlreadyShowed(false);
  }, []);

  /**
   * Close TermsOfUseModal
   * @return {void}
   */
  function closeModal(): void {
    setModal(false);
  }

  /**
   * getStartedTime
   * @return {void}
   */
  function getStartedTime(): void {
    if (
      metaDataTimes.startedForm === undefined ||
      metaDataTimes.startedForm === ""
    ) {
      setMetaDataTimes({
        ...metaDataTimes,
        startedForm: String(new Date().valueOf()),
      });
    }
  }

  /**
   * update Metadata
   * @return {void}
   */
  function getadvancedToStepTwo(): void {
    setMetaDataTimes({
      ...metaDataTimes,
      advancedToStepTwo: String(new Date().valueOf()),
    });
  }

  /**
   * getMetaData
   * @return {IFormMetaData}
   */
  function getMetaData(): IFormMetaData {
    return {
      date: new Date().toLocaleString("pt-br"),
      timezone: new Date().getTimezoneOffset() / 60,
      hostname: document.location.hostname,
      pathname: document.location.pathname,
      referrer: document.referrer,
      historyLength: history.length,
      queryParams: query,
      cookies: document.cookie,
      localstorage: localStorage,
      formData: {
        startedForm: metaDataTimes.startedForm,
        acceptedPrivacyAndPolicyTerms:
          metaDataTimes.acceptedPrivacyAndPolicyTerms,
        advancedToStepTwo: metaDataTimes.advancedToStepTwo,
        acceptedPartnerDataTerms: metaDataTimes.acceptedPartnerDataTerms,
        advancedToStepThree: metaDataTimes.advancedToStepThree,
        finishedForm: metaDataTimes.finishedForm,
      },
      browser: {
        appCodeName: infos.browser.name as string,
        appVersion: infos.browser.version as string,
        language: window.navigator.language,
        vendor: window.navigator.vendor,
      },
      os: {
        platform: infos.os.name as string,
      },
    };
  }

  /**
   * Increment admsList keys to organize and filter with autocomplete cmp
   */
  const options = admsList.map((adm) => {
    const firstLetter = adm.name ? adm.name[0].toUpperCase() : "";
    return {
      firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
      label: adm.name ? adm.name : "",
      ...adm,
    };
  });

  const fetchGroupOptions = async (admId: string): Promise<string[]> => {
    try {
      const response = await listGroups(admId);
      const groups = response.data.groups as Array<{ number: string }>;
      return groups.map((group) => group.number);
    } catch (error) {
      console.error("Error fetching groups:", error);
      return [];
    }
  };

  /**
   *
   * @param values
   */
  async function submitForm(values: any) {
    setIsLoading(true);
    const currentAdm =
      admsList.length > 1
        ? admsList.find((adm) => {
            return adm.id === String(values.admId);
          })
        : admsList[0];

    try {
      // Fetch groups before proceeding
      const groups = await fetchGroupOptions(currentAdm?.id || "");

      const formPayload = {
        ...dataForm,
        ...{
          name: values.name,
          email: values.email,
          phone: values.cellPhone,
          companyName: values.companyName,
          isPj: values.isCompany === "y",
          admId: Number(currentAdm?.id),
          admName: currentAdm?.name,
          admSlug: currentAdm?.slug,
          admPatterns: {
            groupPattern: currentAdm?.groupPattern,
            sharePattern: currentAdm?.sharePattern,
          },
          formStep: 1,
          acceptPrivacyPolicy: values.termsOfUse,
          metadata: getMetaData(),
          isAwarded: values.isAwarded,
        },
      };

      setDataForm(formPayload);

      const registerResponse = await register(formPayload);
      const registerResponseData =
        registerResponse.data as unknown as IRegisterResponse;

      if (registerResponseData.id) {
        setDataForm((current) => ({
          ...current,
          ...{
            registrationId: registerResponseData.id,
            traceId: registerResponseData.traceId ?? current.traceId,
            groupOptions: groups as string[],
          },
        }));
        setCurrentAdm(currentAdm || ({} as IAdm));
        setCurrentFormStep(1);
        setIsPj(values.isCompany === "y");
        const shouldAskForStatement =
          currentAdm?.askForStatement || values.isAwarded === "y";
        setAskForStatement(shouldAskForStatement);
        if (
          currentAdm?.isPartner &&
          !shouldAskForStatement &&
          groups.length > 0
        ) {
          setCurrentFormVariation("partner");
        } else if (
          currentAdm?.isPartner &&
          shouldAskForStatement &&
          groups.length > 0
        ) {
          setCurrentFormVariation("partnerWithStatement");
        } else if (currentAdm?.workable && groups.length > 0) {
          setCurrentFormVariation("workable");
        } else {
          setCurrentFormVariation("nonworkable");
        }
        getadvancedToStepTwo();
      } else {
        throw new Error("registrationId não foi recebido");
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const inputRef = useRef(null) as any;
  useEffect(() => {
    if (inputRef.current) {
      const height = inputRef?.current?.offsetHeight;
      window.parent.postMessage(
        ["setHeight", `${height + (height * 54) / 100}`],
        "*"
      );
    }
  }, []);

  return (
    <>
      <TermsOfUseModal open={modal} closeModal={closeModal} />

      <Formik
        enableReinitialize
        initialValues={INITIAL_FORM_STATE}
        validationSchema={FORM_VALIDATION}
        onSubmit={(values) => {
          submitForm(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
        }) => {
          return (
            <Form ref={inputRef} id="formPersonalDetails">
              <Grid2 container spacing={3}>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="name"
                    name="name"
                    label="Seu nome completo"
                    value={values.name}
                    onFocus={getStartedTime}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <MaskedTextField
                    mask={cellPhoneMask}
                    onChange={(phone: any) => {
                      handleChange(phone);
                      const inputValue = phone.target.value;
                      updatePhoneMask(inputValue, setCellPhoneMask);
                    }}
                    onFocus={getStartedTime}
                    onBlur={handleBlur}
                    value={values.cellPhone}
                    placeholder="(99) 9999-9999"
                    name="cellPhone"
                  >
                    <TextField
                      fullWidth
                      id="cellPhone"
                      name="cellPhone"
                      label="Seu celular"
                      slotProps={{ input: { inputMode: "numeric" } }}
                      error={touched.cellPhone && Boolean(errors.cellPhone)}
                      helperText={touched.cellPhone && errors.cellPhone}
                    />
                  </MaskedTextField>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Seu email"
                    type="email"
                    value={values.email}
                    onFocus={getStartedTime}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                  <FormGroup>
                    {options.length == 1 ? (
                      <TextField
                        id="admId"
                        value={options[0].label}
                        disabled
                        fullWidth
                        name="admId"
                        label="Qual a administradora do seu consórcio?"
                        error={touched.admId && Boolean(errors.admId)}
                        helperText={touched.admId && errors.admId}
                      />
                    ) : (
                      <Autocomplete
                        id="admId"
                        getOptionKey={(option) =>
                          option.id + "_" + option.label
                        }
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          String(option.id) === String(value?.id)
                        }
                        groupBy={(option) => option.firstLetter}
                        options={options.sort(
                          (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
                        )}
                        value={
                          options.find(
                            (option) =>
                              String(option.id) === String(values.admId) &&
                              option.name?.toUpperCase() === admName
                          ) || null
                        }
                        onChange={(_e, value) => {
                          setAdmName(value?.name?.toUpperCase() || "");
                          setFieldValue("admId", value?.id);
                        }}
                        onOpen={handleBlur}
                        onFocus={getStartedTime}
                        noOptionsText="Opção não existente"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name="admId"
                            label="Qual a administradora do seu consórcio?"
                            error={touched.admId && Boolean(errors.admId)}
                            helperText={touched.admId && errors.admId}
                          />
                        )}
                      />
                    )}
                  </FormGroup>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <FormLabel
                      id="isCompanyLabel"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      O consórcio está em nome de{" "}
                      <strong>pessoa jurídica?</strong>
                    </FormLabel>
                    <RadioGroup
                      row
                      name="isCompany"
                      id="isCompany"
                      aria-labelledby="isCompanyLabel"
                      value={values.isCompany}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="y"
                        control={<Radio />}
                        label="Sim"
                      />
                      <FormControlLabel
                        value="n"
                        control={<Radio />}
                        label="Não"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <FormLabel id="isAwarded" sx={{ whiteSpace: "nowrap" }}>
                      Seu consórcio está <strong>contemplado?</strong>
                    </FormLabel>
                    <RadioGroup
                      row
                      name="isAwarded"
                      id="isAwarded"
                      aria-labelledby="isAwarded"
                      value={values.isAwarded}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="y"
                        control={<Radio />}
                        label="Sim"
                      />
                      <FormControlLabel
                        value="n"
                        control={<Radio />}
                        label="Não"
                      />
                    </RadioGroup>
                    <ErrorMessage
                      name="isAwarded"
                      render={(msg: string) => (
                        <p className="errorMessage">{msg}</p>
                      )}
                    />
                  </FormControl>
                </Grid2>

                {values.isCompany === "y" && (
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      id="companyName"
                      name="companyName"
                      label="Nome da empresa"
                      value={values.companyName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                    />
                  </Grid2>
                )}

                <Grid2 size={{ xs: 12, sm: 9 }}>
                  <FormControl>
                    <SpanStyledContainer>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="termsOfUse"
                            id="termsOfUse"
                            value={values.termsOfUse}
                            checked={values.termsOfUse}
                            onBlur={handleBlur}
                            onChange={(e) => {
                              setMetaDataTimes({
                                ...metaDataTimes,
                                acceptedPrivacyAndPolicyTerms: String(
                                  new Date().valueOf()
                                ),
                              });
                              handleChange(e);
                            }}
                          />
                        }
                        label={<></>}
                        sx={{
                          margin: "0px ",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "end",
                          maxHeight: "30px",
                          maxWidth: "30px",
                        }}
                      />
                      <SpanStyled>
                        Concordo com os
                        <LinkStyled onClick={() => setModal(true)}>
                          Termos de Uso
                        </LinkStyled>
                      </SpanStyled>
                    </SpanStyledContainer>
                    <ErrorMessage
                      name="termsOfUse"
                      render={(msg: string) => (
                        <p className="errorMessage">{msg}</p>
                      )}
                    />
                  </FormControl>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 3 }}>
                  <WrapperStyled>
                    <LoadingButton
                      fullWidth
                      loading={isLoading}
                      variant="goFoward"
                      type="submit"
                    >
                      continuar
                    </LoadingButton>
                  </WrapperStyled>
                </Grid2>
              </Grid2>
              {/* <pre>{JSON.stringify(errors, null, 4)}</pre>*/}
              {/* <pre>{JSON.stringify(values, null, 4)}</pre> */}
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
